// handleDownloadExcel.js - ExcelJS version
import ExcelJS from 'exceljs';
import { getComparisonRows } from './getComparisonRows';
import { getBeaconStatus } from './getBeaconStatus';

// Style definitions (ExcelJS format)
const styles = {
  header: {
    font: { bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF211747' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
  },
  title: {
    font: { bold: true, size: 14 },
    alignment: { horizontal: 'left' },
  },
  label: {
    font: { bold: true },
    alignment: { horizontal: 'left' },
  },
  pass: {
    font: { color: { argb: 'FF2E7D32' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } },
  },
  fail: {
    font: { color: { argb: 'FFC62828' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEE' } },
  },
  missing: {
    font: { color: { argb: 'FFE65100' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } },
  },
  extra: {
    font: { color: { argb: 'FF1565C0' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } },
  },
  notValidated: {
    font: { color: { argb: 'FF757575' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } },
  },
  staticField: {
    font: { bold: true },
  },
  extraCustomAttribute: {
    font: { bold: true, color: { argb: 'FFD32F2F' } },
  },
};

// Helper to apply style to a cell
function applyStyle(cell, style) {
  if (!style) return;
  if (style.font) cell.font = style.font;
  if (style.fill) cell.fill = style.fill;
  if (style.alignment) cell.alignment = style.alignment;
}

export async function handleDownloadExcel(
  sortedApiReturn,
  includeNonValidated,
  includeOutboundBeacons,
  regressionTag,
) {
  // Create a new workbook
  const wb = new ExcelJS.Workbook();

  // Filter the data based on settings
  let exportList = sortedApiReturn;

  // Separate missing Scenarios
  const missingEvents = exportList.filter((b) => b.isMissing);
  const nonMissingEvents = exportList.filter((b) => !b.isMissing);

  // Apply filters to non-missing Scenarios only
  let filteredNonMissing = nonMissingEvents;

  if (!includeNonValidated) {
    filteredNonMissing = filteredNonMissing.filter(
      (b) => b.truth.status !== 404 && b.beaconData.status !== 404,
    );
  }

  if (!includeOutboundBeacons) {
    filteredNonMissing = filteredNonMissing.filter((b) => b.beaconType === 'inbound');
  }

  // For missing Scenarios, only include inbound if outbound filter is off
  let filteredMissing = missingEvents;
  if (!includeOutboundBeacons) {
    filteredMissing = filteredMissing.filter((b) => b.beaconType === 'inbound');
  }

  // Combine filtered missing Scenarios with filtered non-missing Scenarios
  exportList = [...filteredMissing, ...filteredNonMissing].sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  // Get test metadata
  const market = JSON.parse(localStorage.getItem('market') || '{}');
  const marketName = market.name || 'Unknown Market';
  const flowName = localStorage.getItem('lastFlow') || 'Unknown Flow';
  const startTime = localStorage.getItem('startTimeLocal') || 'N/A';
  const endTime = localStorage.getItem('endTimeLocal') || 'N/A';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get selected device information
  const selectedDevice = JSON.parse(localStorage.getItem('selectedDevice') || '{}');
  const deviceName = selectedDevice.name || 'No Device';
  const deviceId = selectedDevice.id || 'N/A';

  // Sheet 1: Summary
  const summarySheetName = regressionTag ? `Summary-${regressionTag}`.slice(0, 31) : 'Summary';
  const summarySheet = wb.addWorksheet(summarySheetName);

  // Set column widths
  summarySheet.columns = [{ width: 25 }, { width: 40 }];

  // Title
  let row = summarySheet.addRow(['Beacon Validation Report']);
  applyStyle(row.getCell(1), styles.title);
  summarySheet.addRow([]);

  // Test Information
  row = summarySheet.addRow(['Test Information', '']);
  applyStyle(row.getCell(1), styles.label);
  summarySheet.addRow(['Flow Name:', flowName]);
  summarySheet.addRow(['Market:', marketName]);
  summarySheet.addRow(['Device Name:', deviceName]);
  summarySheet.addRow(['Device ID:', deviceId]);
  summarySheet.addRow(['Regression Tag:', regressionTag || 'N/A']);
  summarySheet.addRow([`Test Start:`, `${startTime} (${timezone})`]);
  summarySheet.addRow([`Test End:`, `${endTime} (${timezone})`]);
  summarySheet.addRow([]);

  // Scenario Counts
  row = summarySheet.addRow(['Scenario Summary', '']);
  applyStyle(row.getCell(1), styles.label);
  summarySheet.addRow(['Total Scenarios:', exportList.length]);
  summarySheet.addRow([
    'Inbound Scenarios:',
    exportList.filter((b) => b.beaconType === 'inbound').length,
  ]);
  summarySheet.addRow([
    'Outbound Scenarios:',
    exportList.filter((b) => b.beaconType !== 'inbound').length,
  ]);
  summarySheet.addRow([]);

  // Status Summary
  const passedCount = exportList.filter((b) => getBeaconStatus(b) === 'PASS').length;

  const failedCount = exportList.filter((b) => getBeaconStatus(b) === 'FAIL').length;

  const missingCount = exportList.filter((b) => getBeaconStatus(b) === 'MISSING').length;

  const notValidatedCount = exportList.filter((b) => getBeaconStatus(b) === 'NOT_VALIDATED').length;

  const notPresentCount = exportList.filter(
    (b) => getBeaconStatus(b) === 'NOT_PRESENT_IN_APP',
  ).length;

  summarySheet.addRow(['Passed:', passedCount]);
  summarySheet.addRow(['Failed:', failedCount]);
  summarySheet.addRow(['Missing Scenarios:', missingCount]);
  summarySheet.addRow(['Not Validated:', notValidatedCount]);
  summarySheet.addRow(['Not Present In App:', notPresentCount]);
  summarySheet.addRow(['Sequence Issues:', exportList.filter((b) => b.hasOrderIssue).length]);

  // Sheet 2: Scenarios Overview
  const eventsSheet = wb.addWorksheet('Scenarios Overview');

  // Set column widths
  eventsSheet.columns = [
    { width: 25 }, // Date/Time
    { width: 30 }, // Scenario Name
    { width: 20 }, // Scenario Type
    { width: 15 }, // Device ID
    { width: 25 }, // Event Name
    { width: 12 }, // Expected Seq
    { width: 12 }, // Actual Seq
    { width: 15 }, // Status
    { width: 40 }, // Notes
  ];

  // Header row
  const headerRow = eventsSheet.addRow([
    'Date/Time',
    'Scenario Name',
    'Scenario Type',
    'Device ID',
    'Event Name',
    'Expected Seq',
    'Actual Seq',
    'Status',
    'Notes',
  ]);
  headerRow.eachCell((cell) => applyStyle(cell, styles.header));

  exportList.forEach((beacon) => {
    // Determine overall status and style

    const status = getBeaconStatus(beacon);

    const statusLabelMap = {
      PASS: 'Pass',
      FAIL: 'Fail',
      MISSING: 'Missing Event',
      NOT_VALIDATED: 'Not Validated',
      NOT_PRESENT_IN_APP: 'Not Present In App',
    };

    const overallStatus = statusLabelMap[status] || status;

    const statusStyleMap = {
      PASS: styles.pass,
      FAIL: styles.fail,
      MISSING: styles.missing,
      NOT_VALIDATED: styles.notValidated,
      NOT_PRESENT_IN_APP: styles.notValidated,
    };

    const statusStyle = statusStyleMap[status] || {};

    const eventType =
      beacon.beaconType === 'inbound' ? 'Inbound' : `Outbound (${beacon.beaconType})`;

    const formattedDate = beacon.isMissing
      ? 'Missing'
      : new Date(beacon.timestamp).toLocaleString('en-US', {
          timeZoneName: 'short',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

    const dataRow = eventsSheet.addRow([
      formattedDate,
      beacon.eventName || beacon.beaconID,
      eventType,
      deviceId,
      beacon.stepName || '',
      beacon.stepNumber || '',
      beacon.actualSequence || '',
      overallStatus,
      beacon.notes || '',
    ]);

    // Apply status style to the status cell
    applyStyle(dataRow.getCell(8), statusStyle);
  });

  // Add autofilter
  eventsSheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: eventsSheet.rowCount, column: 9 },
  };

  // Pre-process sheet names
  const sheetNames = new Map();
  const usedNames = new Set(['Summary', summarySheetName, 'Scenarios Overview']);

  exportList.slice(0, 50).forEach((beacon, index) => {
    let baseName = beacon.stepName || beacon.eventName || `Scenario_${index + 1}`;
    let cleanName = baseName
      .replace(/[\\/*?:[\]]/g, '_')
      .trim()
      .slice(0, 25);

    if (usedNames.has(cleanName)) {
      cleanName = `${cleanName.slice(0, 20)}_${index + 1}`;
    }

    usedNames.add(cleanName);
    sheetNames.set(index, cleanName);
  });

  // Sheet 3+: Individual Scenario Details (limit to 50 sheets)
  exportList.slice(0, 50).forEach((beacon, index) => {
    const sheetName = sheetNames.get(index);
    const eventSheet = wb.addWorksheet(sheetName);

    // Scenario header information
    row = eventSheet.addRow(['Scenario Details']);
    applyStyle(row.getCell(1), styles.title);
    eventSheet.addRow([]);

    row = eventSheet.addRow(['Scenario Name:', beacon.eventName || beacon.beaconID]);
    applyStyle(row.getCell(1), styles.label);

    row = eventSheet.addRow([
      'Scenario Type:',
      beacon.beaconType === 'inbound' ? 'Inbound' : `Outbound (${beacon.beaconType})`,
    ]);
    applyStyle(row.getCell(1), styles.label);

    row = eventSheet.addRow([
      'Timestamp:',
      beacon.isMissing
        ? 'Missing'
        : new Date(beacon.timestamp).toLocaleString('en-US', { timeZoneName: 'short' }),
    ]);
    applyStyle(row.getCell(1), styles.label);

    row = eventSheet.addRow(['Device Name:', deviceName]);
    applyStyle(row.getCell(1), styles.label);

    row = eventSheet.addRow(['Device ID:', deviceId]);
    applyStyle(row.getCell(1), styles.label);

    row = eventSheet.addRow(['Event Name:', beacon.stepName || beacon.eventName || 'N/A']);
    applyStyle(row.getCell(1), styles.label);

    // Determine overall status with styling
    const status = getBeaconStatus(beacon);

    const statusLabelMap = {
      PASS: 'Pass',
      FAIL: 'Fail',
      MISSING: 'Missing Event',
      NOT_VALIDATED: 'Not Validated',
      NOT_PRESENT_IN_APP: 'Not Present In App',
    };

    const overallStatus = statusLabelMap[status] || status;

    const statusStyleMap = {
      PASS: styles.pass,
      FAIL: styles.fail,
      MISSING: styles.missing,
      NOT_VALIDATED: styles.notValidated,
      NOT_PRESENT_IN_APP: styles.notValidated,
    };

    const statusStyle = statusStyleMap[status] || {};

    row = eventSheet.addRow(['Overall Status:', overallStatus]);
    applyStyle(row.getCell(1), styles.label);
    applyStyle(row.getCell(2), statusStyle);

    if (beacon.notes) {
      row = eventSheet.addRow(['Notes:', beacon.notes]);
      applyStyle(row.getCell(1), styles.label);
    }

    eventSheet.addRow([]);
    row = eventSheet.addRow(['Attribute Comparison']);
    applyStyle(row.getCell(1), styles.title);
    eventSheet.addRow([]);

    // Check if this was a "not validated" Scenario
    const currentStatus = getBeaconStatus(beacon);

    const showActualOnlyStatuses = ['NOT_VALIDATED', 'NOT_PRESENT_IN_APP'];

    const wasNotValidated = showActualOnlyStatuses.includes(currentStatus);

    if (beacon.isMissing) {
      // For missing Scenarios, show what was expected
      const attrHeaderRow = eventSheet.addRow(['Variable Name', 'Expected Value', 'Status']);
      attrHeaderRow.eachCell((cell) => applyStyle(cell, styles.header));

      eventSheet.columns = [{ width: 40 }, { width: 40 }, { width: 15 }];

      if (beacon.truth && beacon.truth.data) {
        Object.entries(beacon.truth.data)
          .filter(([key]) => key !== 'market')
          .forEach(([key, val]) => {
            const expectedValue = val && val.value !== undefined ? val.value : val;
            const dataRow = eventSheet.addRow([key, expectedValue, 'Missing']);
            applyStyle(dataRow.getCell(1), styles.staticField);
            applyStyle(dataRow.getCell(3), styles.missing);
          });
      }
    } else if (wasNotValidated) {
      // For not validated Scenarios, show actual values only
      const attrHeaderRow = eventSheet.addRow(['Variable Name', 'Actual Value', 'Status']);
      attrHeaderRow.eachCell((cell) => applyStyle(cell, styles.header));

      eventSheet.columns = [{ width: 40 }, { width: 50 }, { width: 15 }];

      Object.entries(beacon.beaconData.data)
        .filter(([key]) => key !== 'market')
        .forEach(([key, val]) => {
          const actual = val instanceof Object ? val.value : val;
          const dataRow = eventSheet.addRow([key, actual, 'Extra']);
          applyStyle(dataRow.getCell(3), styles.extra);
        });
    } else {
      // For validated Scenarios, show full comparison
      const attrHeaderRow = eventSheet.addRow([
        'Variable Name',
        'Expected Value',
        'Actual Value',
        'Status',
        'Type',
      ]);
      attrHeaderRow.eachCell((cell) => applyStyle(cell, styles.header));

      eventSheet.columns = [
        { width: 40 },
        { width: 30 },
        { width: 30 },
        { width: 15 },
        { width: 15 },
      ];

      const rows = getComparisonRows(beacon.truth, beacon.beaconData, beacon.modifiedRows || {});

      // Sort: custom attributes first, then others, then Extra attributes last
      rows.sort((a, b) => {
        if (a.passFail === 'Extra' && b.passFail !== 'Extra') return 1;
        if (a.passFail !== 'Extra' && b.passFail === 'Extra') return -1;

        const aPr = a.variableName.includes('data.custom_attributes') ? 0 : 1;
        const bPr = b.variableName.includes('data.custom_attributes') ? 0 : 1;
        if (aPr !== bPr) return aPr - bPr;

        return a.variableName.localeCompare(b.variableName);
      });

      const attrStartRow = eventSheet.rowCount;

      rows.forEach((rowData) => {
        let cellStatusStyle = {};
        if (rowData.passFail === 'Pass') cellStatusStyle = styles.pass;
        else if (rowData.passFail === 'Fail') cellStatusStyle = styles.fail;
        else if (rowData.passFail === 'Missing') cellStatusStyle = styles.missing;
        else if (rowData.passFail === 'Extra') cellStatusStyle = styles.extra;

        const shouldBold = rowData.passFail !== 'Extra';
        const isCustomAttribute = rowData.variableName.includes('data.custom_attributes');
        const isExtra = rowData.passFail === 'Extra';
        const shouldBeRed = isCustomAttribute && isExtra;

        const dataRow = eventSheet.addRow([
          rowData.variableName,
          rowData.expectedValue,
          rowData.actualValue,
          rowData.passFail,
          rowData.fieldType,
        ]);

        if (shouldBeRed) {
          applyStyle(dataRow.getCell(1), styles.extraCustomAttribute);
        } else if (shouldBold) {
          applyStyle(dataRow.getCell(1), styles.staticField);
        }
        applyStyle(dataRow.getCell(4), cellStatusStyle);
      });

      // Add autofilter to the comparison table
      if (rows.length > 0) {
        eventSheet.autoFilter = {
          from: { row: attrStartRow, column: 1 },
          to: { row: eventSheet.rowCount, column: 5 },
        };
      }
    }
  });

  // Generate the Excel file
  const buffer = await wb.xlsx.writeBuffer();

  // Create blob and trigger download
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  // Generate filename with timestamp
  const now = new Date();
  const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
  link.setAttribute('download', `beacon_validation_${timestamp}.xlsx`);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
